import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/utils';
import {
  Users, Activity, Zap, BookOpen, BarChart2, TrendingUp,
  Target, CheckCircle, Flame, Award, Globe, Plane, FileText, Database
} from 'lucide-react';

export const ReportsContent = ({ students }) => {
  const totalMiles = students.reduce((sum, s) => sum + (s.miles || 0), 0);
  const totalLessons = students.reduce((sum, s) => sum + (s.completed_lessons || 0), 0);
  const avgMiles = students.length > 0 ? Math.round(totalMiles / students.length) : 0;
  const activeStudents = students.filter(s => (s.streak || 0) > 0).length;
  
  const levelCounts = students.reduce((acc, s) => {
    const level = s.current_level || 'A0';
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Raporlar & Analiz</h2>
          <p className="text-gray-500">Detaylı platform raporları ve öğrenci analitiği</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm">
            <FileText className="w-4 h-4 mr-2" />
            PDF İndir
          </Button>
          <Button variant="outline" size="sm">
            <Database className="w-4 h-4 mr-2" />
            Excel İndir
          </Button>
        </div>
      </div>

      {/* Özet Kartları */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-100">Toplam Öğrenci</p>
                <p className="text-3xl font-bold">{students.length}</p>
              </div>
              <Users className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-100">Aktif Öğrenci</p>
                <p className="text-3xl font-bold">{activeStudents}</p>
              </div>
              <Activity className="w-10 h-10 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-100">Toplam Mil</p>
                <p className="text-3xl font-bold">{totalMiles.toLocaleString()}</p>
              </div>
              <Zap className="w-10 h-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-100">Toplam Ders</p>
                <p className="text-3xl font-bold">{totalLessons.toLocaleString()}</p>
              </div>
              <BookOpen className="w-10 h-10 text-orange-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Detaylı Raporlar */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Seviye Dağılımı Raporu */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-blue-500" />
              Seviye Dağılımı Raporu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {['A0', 'A1', 'A2', 'B1', 'B2', 'C1'].map(level => {
                const count = levelCounts[level] || 0;
                const percentage = students.length > 0 ? Math.round((count / students.length) * 100) : 0;
                return (
                  <div key={level} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-700">{level} Seviyesi</span>
                      <span className="text-gray-500">{count} öğrenci ({percentage}%)</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-blue-400 to-blue-600 h-3 rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Performans Metrikleri */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Performans Metrikleri
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Ortalama Mil</p>
                    <p className="text-sm text-gray-500">Öğrenci başına</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-blue-600">{avgMiles.toLocaleString()}</p>
              </div>
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Tamamlama Oranı</p>
                    <p className="text-sm text-gray-500">Aktif öğrenci yüzdesi</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-green-600">{students.length > 0 ? Math.round((activeStudents / students.length) * 100) : 0}%</p>
              </div>
              <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                    <Flame className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">En Yüksek Seri</p>
                    <p className="text-sm text-gray-500">Günlük çalışma</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-orange-600">{Math.max(...students.map(s => s.streak || 0), 0)} gün</p>
              </div>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">En Yüksek Mil</p>
                    <p className="text-sm text-gray-500">Tek öğrenci</p>
                  </div>
                </div>
                <p className="text-2xl font-bold text-purple-600">{Math.max(...students.map(s => s.miles || 0), 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Eğitim Raporları */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-blue-500" />
              İngilizce Eğitimi Raporu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl text-center">
                  <p className="text-3xl font-bold text-blue-600">6</p>
                  <p className="text-sm text-gray-500">Seviye (A0-C1)</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl text-center">
                  <p className="text-3xl font-bold text-blue-600">250</p>
                  <p className="text-sm text-gray-500">Toplam Ders</p>
                </div>
              </div>
              <div className="space-y-2 pt-2">
                {[
                  { level: 'A0-A1', status: 'Başlangıç', color: 'bg-green-500' },
                  { level: 'A2-B1', status: 'Orta', color: 'bg-blue-500' },
                  { level: 'B2-C1', status: 'İleri', color: 'bg-purple-500' },
                ].map(item => (
                  <div key={item.level} className="flex items-center gap-3 p-2">
                    <div className={cn("w-3 h-3 rounded-full", item.color)} />
                    <span className="font-medium text-gray-700">{item.level}</span>
                    <span className="text-gray-500 text-sm">({item.status})</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-sky-500" />
              Kabin Memurluğu Raporu
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl text-center">
                  <p className="text-3xl font-bold text-sky-600">16</p>
                  <p className="text-sm text-gray-500">Modül</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl text-center">
                  <p className="text-3xl font-bold text-sky-600">85</p>
                  <p className="text-sm text-gray-500">Ünite</p>
                </div>
              </div>
              <div className="space-y-2 pt-2">
                {[
                  { name: 'Mülakat Hazırlık', modules: 6 },
                  { name: 'Havayolu Bilgisi', modules: 4 },
                  { name: 'Teknik Eğitim', modules: 6 },
                ].map(item => (
                  <div key={item.name} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">{item.name}</span>
                    <span className="text-sky-600 font-medium">{item.modules} modül</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportsContent;
